import {css, StyleSheet} from 'aphrodite';
import Slider from 'rc-slider';
import React, {ReactNode} from 'react';

import {assertAndContinue} from 'Asserts';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {BLUE_LIGHT, GRAY_LIGHT} from 'ui-library/Colors';

const DISPLAY_DOTS_THRESHOLD = 25;

interface SliderLabelsProps {
  minLabel: ReactNode;
  maxLabel: ReactNode;
}
export interface LabeledSliderProps {
  min: number;
  minLabel: ReactNode;
  max: number;
  maxLabel: ReactNode;
  onChange: (newValue: number) => void;
  step: number;
  value: number;
}

export function SliderLabels({minLabel, maxLabel}: SliderLabelsProps): JSX.Element {
  return (
    <div className={css(styles.labelsWrapper)}>
      <div className={css(styles.labelText)}>{minLabel}</div>
      <div className={css(styles.labelText, styles.maxLabelText)}>{maxLabel}</div>
    </div>
  );
}

/**
 * Component that allows users to select an option from a custom-defined discrete input range using a horizontal slider.
 *
 * The minimum and maximum of the slider are labeled with minLabel and maxLabel.
 * The inclusive min, max, and step params define how many options the slider has.
 * The step parameter must be greater than zero, and (max - min) should be evenly divisible by the step value.
 *
 * @storybook https://dev--622a4c57262ed1003a5a57fa.chromatic.com/?path=/story/ui-library-inputs-fields-labeledslider--basic
 */
export default function LabeledSlider({
  min,
  minLabel,
  max,
  maxLabel,
  onChange,
  step,
  value,
}: LabeledSliderProps): JSX.Element {
  assertAndContinue(step > 0, 'step parameter must be greater than 0');
  assertAndContinue((max - min) % step === 0, 'max - min should be evenly divisible by the step value');
  assertAndContinue(min <= value && value <= max, 'value must be between min and max (inclusive)');
  assertAndContinue((value - min) % step === 0, 'value must equal min + step * n');
  return (
    <>
      <Slider
        range={true}
        // When there are too many dots, they won't be distinguishable on the slider, so just don't show them
        dots={(max - min) / step < DISPLAY_DOTS_THRESHOLD}
        min={min}
        max={max}
        onChange={onChange}
        step={step}
        value={value}
        handleStyle={{
          backgroundColor: BLUE_LIGHT,
          border: 0,
          boxShadow: 'none',
          marginTop: -4,
          height: 12,
          width: 12,
        }}
        dotStyle={{backgroundColor: GRAY_LIGHT, border: 0}}
        railStyle={{backgroundColor: GRAY_LIGHT}}
      />
      <SliderLabels minLabel={minLabel} maxLabel={maxLabel} />
    </>
  );
}

const styles = StyleSheet.create({
  labelsWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  labelText: {
    ...FONT_PROXIMA_13,
    lineHeight: '16px',
  },
  maxLabelText: {
    textAlign: 'right',
  },
});
